import React, { useEffect, useRef } from "react";
import { FiTarget, FiTrendingUp, FiZap, FiClock, FiBarChart2, FiRepeat, FiArrowRight } from "react-icons/fi";
import "./HowWeDriveOutcomes.css";

const HowWeDriveOutcomes = () => {
  const canvasRef = useRef(null);

  const steps = [
    {
      number: "01",
      title: "Define",
      description: "We co-create a bold vision and define the right transformation architecture.",
      icon: FiTarget
    },
    {
      number: "02",
      title: "Architect",
      description: "We align strategy, operating model, data, AI and technology into a single, integrated blueprint.",
      icon: FiTrendingUp
    },
    {
      number: "03",
      title: "Engineer",
      description: "We build, scale and embed solutions that deliver measurable, lasting impact.",
      icon: FiZap
    }
  ];

  const metrics = [
    { text: "Weeks to alignment (not months)", icon: FiClock },
    { text: "Quarterly impact (not annual reviews)", icon: FiBarChart2 },
    { text: "Built to scale, not just to pilot", icon: FiRepeat }
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.5,
      dx: (Math.random() - 0.5) * 0.15,
      dy: (Math.random() - 0.5) * 0.15,
      opacity: Math.random() * 0.2 + 0.05,
    }));

    let animationId;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((a, i) => {
        particles.slice(i + 1).forEach((b) => {
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(194, 161, 90, ${0.04 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(194, 161, 90, ${p.opacity})`;
        ctx.fill();
        
        p.x += p.dx;
        p.y += p.dy;
        
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section className="how-we-drive">
      <canvas ref={canvasRef} className="how-we-drive__canvas" />
      
      <div className="how-we-drive__container">
        {/* Left Side - White Section */}
        <div className="how-we-drive__left">
          <div className="how-we-drive__header">
            <p className="how-we-drive__eyebrow">HOW WE DRIVE OUTCOMES</p>
          </div>

          <div className="how-we-drive__steps-horizontal">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <div className="how-we-drive__step-card">
                  <div className="how-we-drive__step-number">{step.number}</div>
                  <div className="how-we-drive__step-icon-wrapper">
                    <step.icon className="how-we-drive__step-icon" />
                  </div>
                  <div className="how-we-drive__step-title">{step.title}</div>
                  <div className="how-we-drive__step-description">{step.description}</div>
                </div>
                {index < steps.length - 1 && (
                  <div className="how-we-drive__step-arrow">
                    <FiArrowRight />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Right Side - Blue Box Section */}
        <div className="how-we-drive__right">
          <div className="how-we-drive__metrics-box">
            <h3 className="how-we-drive__metrics-title">FROM STRATEGY TO SCALE — FASTER.</h3>
            <div className="how-we-drive__metrics-list">
              {metrics.map((metric, index) => (
                <div key={index} className="how-we-drive__metric-item">
                  <metric.icon className="how-we-drive__metric-icon" />
                  <span>{metric.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowWeDriveOutcomes;